import {
  NovaChatMessage,
  NovaConversationSummary,
  NovaDeleteConversationsResponse,
  NovaGetConversationResponse,
  NovaListConversationsResponse,
  NovaPreferences,
  NovaPreferencesResponse,
  NovaSuggestion,
  NovaSuggestionsResponse,
} from '@/types';

// In-memory stand-ins for the not-yet-built Nova backend. Nothing here is persisted
// across a page reload; it only exists so the frontend can be built and demoed
// against a plausible contract before /services/nova/* is real. Documents aren't
// mocked here — they always hit the real services/gdai/nova/documents endpoints.

// No longer written to anywhere — Nova chat now hits the real AVA stream (see
// api/nova-ava.ts) instead of mockNovaChat, so history stays empty until a real
// /services/nova/listConversations-equivalent backend exists.
const conversations = new Map<string, { title: string; messages: NovaChatMessage[]; dateCreated: string; dateModified: string }>();

let preferences: NovaPreferences = {
  proactiveSuggestionsEnabled: true,
  dismissedSuggestionIds: [],
  documentUploadConsent: false,
};

const suggestions: NovaSuggestion[] = [];

export const mockListNovaConversations = async (): Promise<NovaListConversationsResponse> => {
  const list: NovaConversationSummary[] = Array.from(conversations.entries()).map(([conversationId, c]) => ({
    conversationId,
    title: c.title,
    lastMessagePreview: c.messages[c.messages.length - 1]?.content?.slice(0, 120) ?? '',
    dateCreated: c.dateCreated,
    dateModified: c.dateModified,
  }));

  return { succeeded: true, conversations: list.sort((a, b) => b.dateModified.localeCompare(a.dateModified)) };
};

export const mockGetNovaConversation = async (conversationId: string): Promise<NovaGetConversationResponse> => {
  const conversation = conversations.get(conversationId);

  return {
    succeeded: !!conversation,
    conversationId,
    title: conversation?.title ?? '',
    messages: conversation?.messages ?? [],
  };
};

export const mockDeleteNovaConversations = async (conversationIds: string[]): Promise<NovaDeleteConversationsResponse> => {
  conversationIds.forEach((id) => conversations.delete(id));
  return { succeeded: true };
};

export const mockGetNovaSuggestions = async (): Promise<NovaSuggestionsResponse> => ({
  succeeded: true,
  suggestions: suggestions.filter((s) => !preferences.dismissedSuggestionIds.includes(s.suggestionId)),
});

export const mockDismissNovaSuggestion = async (suggestionId: string): Promise<{ succeeded: boolean }> => {
  if (!preferences.dismissedSuggestionIds.includes(suggestionId)) {
    preferences = { ...preferences, dismissedSuggestionIds: [...preferences.dismissedSuggestionIds, suggestionId] };
  }
  return { succeeded: true };
};

export const mockGetNovaPreferences = async (): Promise<NovaPreferencesResponse> => ({
  succeeded: true,
  preferences,
});

export const mockSaveNovaPreferences = async (next: NovaPreferences): Promise<{ succeeded: boolean }> => {
  preferences = next;
  return { succeeded: true };
};
